import { useEffect, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2, AlertTriangle } from 'lucide-react';
import { useCreateProforma } from '../../hooks/useProformas';
import { Button } from '../../components/ui/Button';

/**
 * Auto-creates a proforma draft for the given project and redirects
 * immediately to the builder. No modal, no navigation to the list.
 * Optional ?setId= param links the proforma to a medida set.
 */
export function CreateProformaAutoPage() {
  const { id: projectId } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const setId = searchParams.get('setId') ?? undefined;
  const navigate = useNavigate();
  const createMutation = useCreateProforma();
  const fired = useRef(false);

  useEffect(() => {
    if (!projectId || fired.current) return;
    fired.current = true;

    createMutation
      .mutateAsync({ projectId, descuento: 0, ivaRate: 15, medidaSetId: setId ?? null })
      .then((result) => {
        navigate(`/proformas/${result.id}`, { replace: true });
      })
      .catch(() => {
        // error state handled below via mutation.isError
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  if (createMutation.isError) {
    return (
      <div className="flex min-h-[40vh] flex-col items-center justify-center gap-4">
        <AlertTriangle className="h-10 w-10 text-red-400" />
        <p className="text-sm text-gray-600">No se pudo crear la proforma. Intenta nuevamente.</p>
        <div className="flex gap-3">
          <Button variant="secondary" onClick={() => navigate(`/proyectos/${projectId}`)}>
            Volver al proyecto
          </Button>
          <Button
            variant="primary"
            onClick={() => {
              fired.current = false;
              createMutation
                .mutateAsync({ projectId: projectId!, descuento: 0, ivaRate: 15, medidaSetId: setId ?? null })
                .then((result) => navigate(`/proformas/${result.id}`, { replace: true }))
                .catch(() => {});
            }}
          >
            Reintentar
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-[40vh] flex-col items-center justify-center gap-3">
      <Loader2 className="h-8 w-8 animate-spin text-brand-blue" />
      <p className="text-sm text-gray-500">Creando proforma...</p>
    </div>
  );
}
